import React, { forwardRef, useCallback, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';

// GLOBAL VARIABLES
import { NUMBER_FORMATS } from 'numberFormats.js';

// CORE COMPONENTS
import Button from 'core/tools/Button';
import Tooltip from 'core/tools/Tooltip';
import NumberFormat from 'core/tools/NumberFormat';

// STYLES
import styles from './copyButton.module.scss';

// MAIN COMPONENT
export const copyButton = Component =>
  forwardRef(
    (
      {
        copyToClipboard, //
        ...props
      },
      ref
    ) => {
      const { placeholder, schema, type, value } = props;

      const [tip, setTip] = useState('Copy to Clipboard');

      const handleClick = useCallback(() => {
        const textValue = typeof value === 'object' ? value.props.children : value;
        navigator.clipboard.writeText(textValue);
        setTip(`Copied to Clipboard: ${textValue}`);
      }, [value, setTip]);

      const handleMouseLeave = useCallback(() => {
        setTimeout(() => setTip('Copy to Clipboard'), 200);
      }, [setTip]);

      const format = NUMBER_FORMATS[schema || type];

      const hasValue = ![undefined, null, false, ''].includes(value);

      // RENDER
      return copyToClipboard ? (
        <Component
          {...props}
          as="span"
          value={
            <span className="d-flex flex-wrap">
              <span className={clsx('me-5', styles.text)}>
                {hasValue ? (
                  !!format ? (
                    <NumberFormat format={format} displayType="text" value={value} ref={ref} />
                  ) : (
                    <span ref={ref}>{value}</span>
                  )
                ) : (
                  placeholder
                )}
              </span>
              {hasValue && (
                <Tooltip placement="top" wrapperClassName={styles.tip} tip={tip}>
                  <Button
                    icon={{
                      use: 'clipboard',
                      order: 1
                    }}
                    className={clsx('text-gray-700', styles.btn)}
                    variant="gray-100"
                    size="sm"
                    label="Copy"
                    onClick={handleClick}
                    onMouseLeave={handleMouseLeave}
                  />
                </Tooltip>
              )}
            </span>
          }
          ref={ref}
        />
      ) : (
        <Component {...props} ref={ref} />
      );
    }
  );
